if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      cancelAnimationFrame(this.hpThumbAlignmentFrame);

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.hpInitialThumbAlignment = true;
      this.hpThumbFilterReady = false;
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      this.addEventListener('keydown', this.handleKeyDown.bind(this));
      this.viewer.addEventListener('click', this.handleViewerClick.bind(this));

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

      // HP custom: variant image filter — replaces StarApps Variant Image app
      this.hpInitVariantFilter();
      this.hpThumbFilterReady = true;
      if (this.thumbs && this.currentItem) this.setActiveThumb();
    }

    /**
     * HP custom: detect variant image filter data, apply initial filter.
     * Auto-no-op when product has no variant-linked images.
     */
    hpInitVariantFilter() {
      this.hpFilterItems = this.querySelectorAll('[data-hp-vids]');
      const hasAny = Array.from(this.hpFilterItems).some((el) => el.dataset.hpVids !== '');
      if (!hasAny) return;
      this.hpFilterEnabled = true;
      const initialVariantInput = this.section.querySelector('input[name="id"][data-dynamic-variant-content], input[name="id"]');
      const initialVariantId = initialVariantInput ? parseInt(initialVariantInput.value, 10) : NaN;
      this.hpApplyVariantFilter(initialVariantId || null);
    }

    /**
     * HP custom: filter gallery items by variant.
     *  - Variant-linked items shown only when their variant_ids include variantId.
     *  - Pre-variant videos/models always stay (shared content).
     *  - Pre-variant images (general shots before the first variant image) stay
     *    visible for every variant — they are shared product images, not variant-
     *    specific. Only the very first image (the title/hero) is hidden once a
     *    variant image shows, as it is typically a duplicate of the selected
     *    variant's own image. This matches StarApps Variant Image Automator, which
     *    shows "common" (unassigned) images for all variants.
     * Always runs (even with null variantId) so the gallery never sticks on a
     * stale variant when the user picks an unavailable option combination.
     * @param {?number} variantId
     */
    hpApplyVariantFilter(variantId) {
      if (!this.hpFilterEnabled) return;

      let anyVariantItemVisible = false;
      this.hpFilterItems.forEach((el) => {
        const raw = el.dataset.hpVids;
        if (raw === '') return;
        const vids = raw.split(',').map(Number);
        const show = variantId && vids.includes(variantId);
        el.classList.toggle('hp-vimg-hidden', !show);
        if (show && el.classList.contains('media-viewer__item')) anyVariantItemVisible = true;
      });

      this.hpFilterItems.forEach((el) => {
        if (el.dataset.hpPre !== 'true') return;
        const isImage = el.dataset.mediaType === 'image';
        // Pre-variant images placed before the first variant image are shared/general
        // product shots (StarApps-era "common" images) and stay visible for every
        // variant. Only the very first image (the title/hero, data-hp-pre-first) is
        // hidden once a variant image shows, since it is typically a duplicate of the
        // selected variant's own image and would otherwise appear twice.
        const isTitle = el.dataset.hpPreFirst === 'true';
        el.classList.toggle('hp-vimg-hidden', isImage && isTitle && anyVariantItemVisible);
      });

      // Refresh slider state. setVisibleItems updates the total counter.
      // For the current counter we can't rely on customSetActiveMedia when the
      // active item is unchanged — it short-circuits at the top (line ~479).
      // So when the current item is still visible, refresh counter directly.
      if (typeof this.setVisibleItems === 'function') this.setVisibleItems();
      if (this.visibleItems && this.visibleItems.length > 0) {
        const currentStillVisible = this.currentItem
          && this.visibleItems.indexOf(this.currentItem) !== -1;
        if (!currentStillVisible) {
          this.customSetActiveMedia(this.visibleItems[0], true);
        } else {
          this.currentIndex = this.visibleItems.indexOf(this.currentItem);
          if (this.counterCurrent) {
            this.counterCurrent.textContent = this.currentIndex + 1;
          }
          if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
          if (this.nextBtn) this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
          // Layout has shifted (28 items just got display:none) — re-align scroll.
          if (this.viewer && typeof this.currentItem.offsetLeft === 'number') {
            this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
          }
        }
      }
      // Nach beiden Filter-Zweigen auch die Leiste im gefilterten Layout ausrichten.
      if (this.thumbs && this.currentItem) this.setActiveThumb();
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      // HP custom: variant image filter — always invoke so an invalid option
      // combination (variant=null) clears stale filtering instead of sticking.
      if (this.hpFilterEnabled) {
        this.hpApplyVariantFilter(evt.detail.variant ? evt.detail.variant.id : null);
      }
      // Speed-AP4 WP2: Bilder der neuen Farbe im Waermer vorziehen.
      this.hpWarmPrioritizeVisible();

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      // Speed-AP4 WP2 (2026-09-01): Der Vendor-Loop kippte hier ALLE lazy Galerie-Bilder per
      // setTimeout auf loading=eager, auch die per display:none (hp-vimg-hidden) versteckten
      // Bilder der anderen Farben -> 45 Bilder / 1.4 MB im LCP-Fenster jeder Produktseite.
      // Ersetzt durch hpScheduleWarm(): bewusster, sequentieller Waermer nach window.load +
      // Leerlauf mit Netz-Guard (Data-Saver/2g: nichts, 3g: nur aktive Farbe, sonst alle
      // Farben), damit der Farbwechsel sofort bleibt. Details docs/VARIANT_IMAGE_FILTER.md.
      if (!this.isFeatured) this.hpScheduleWarm();

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    /**
     * HP custom (Speed-AP4 WP2): Waermer fuer die Galerie-Bilder erst nach window.load
     * und Browser-Leerlauf starten, damit LCP/Interaktivitaet nicht konkurrieren.
     */
    hpScheduleWarm() {
      if (this.hpWarmScheduled) return;
      this.hpWarmScheduled = true;
      const start = () => {
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
        idle(() => this.hpWarmGallery(), { timeout: 4000 });
      };
      if (document.readyState === 'complete') {
        setTimeout(start, 500);
      } else {
        window.addEventListener('load', () => setTimeout(start, 500), { once: true });
      }
    }

    /**
     * Netz-Guard: 'none' (Data-Saver, 2g) | 'active' (3g: nur sichtbare Farbe) | 'all'.
     */
    hpWarmMode() {
      const c = navigator.connection;
      if (!c) return 'all';
      if (c.saveData) return 'none';
      const t = c.effectiveType || '';
      if (t === 'slow-2g' || t === '2g') return 'none';
      if (t === '3g') return 'active';
      return 'all';
    }

    /**
     * Baut die Waerm-Queue: erst restliche Slides der aktiven Farbe (sichtbar), dann die
     * versteckten Farben in DOM-Reihenfolge, zuletzt lazy Thumbs. Sequentiell, ein Bild
     * nach dem anderen (loading=eager auf dem echten <img>, der Browser waehlt damit den
     * korrekten <picture>-Kandidaten; kein Doppel-Download). display:none bleibt unangetastet
     * (Lightbox-Kopplung ueber visibleItems).
     */
    hpWarmGallery() {
      const mode = this.hpWarmMode();
      if (mode === 'none' || !this.viewer) return;
      const items = Array.from(this.viewer.querySelectorAll('.media-viewer__item'));
      const visible = [];
      const hidden = [];
      items.forEach((li) => (li.classList.contains('hp-vimg-hidden') ? hidden : visible).push(li));
      const order = mode === 'all' ? visible.concat(hidden) : visible;
      this.hpWarmQueue = order
        .map((li) => li.querySelector('.product-image[loading="lazy"]'))
        .filter(Boolean);
      if (mode === 'all' && this.thumbs) {
        this.hpWarmQueue = this.hpWarmQueue.concat(
          Array.from(this.thumbs.querySelectorAll('img[loading="lazy"]'))
        );
      }
      this.hpWarmNext();
    }

    hpWarmNext() {
      if (!this.hpWarmQueue || this.hpWarmQueue.length === 0) return;
      const img = this.hpWarmQueue.shift();
      if (!img || img.loading !== 'lazy' || (img.complete && img.naturalWidth > 0)) {
        this.hpWarmNext();
        return;
      }
      let finished = false;
      const done = () => {
        if (finished) return;
        finished = true;
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        this.hpWarmNext();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
      img.loading = 'eager';
      // Sicherheitsnetz: kommt kein load/error (Cache-Sonderfall), nach 4 s weiter.
      setTimeout(done, 4000);
    }

    /**
     * Nach einem Variantenwechsel die Bilder der jetzt sichtbaren Farbe an die Spitze
     * der Queue holen (falls der Waermer noch nicht dort war).
     */
    hpWarmPrioritizeVisible() {
      if (!this.hpWarmQueue || this.hpWarmQueue.length === 0) return;
      const front = [];
      const rest = [];
      this.hpWarmQueue.forEach((img) => {
        const li = img.closest('.media-viewer__item');
        (li && !li.classList.contains('hp-vimg-hidden') ? front : rest).push(img);
      });
      this.hpWarmQueue = front.concat(rest);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      // Guard: with 0/1 visible item there is no [1] to measure (e.g. a variant
      // whose image filter leaves a single image). Without this, scroll/resize
      // threw `undefined is not an object (this.visibleItems[1].offsetLeft)`
      // ~135×/3 days in Clarity (Tobias 2026-06-06). Isolated handler — never
      // blocked add-to-cart, but noisy + could glitch the gallery.
      if (!this.visibleItems || this.visibleItems.length <= 1) return;
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      if (!itemToShow) return; // at the first/last item — nothing to scroll to
      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Fokussiert den Viewer, wenn ein nicht interaktiver Bereich des Hauptbilds
     * angeklickt wird. Links und Mediensteuerungen behalten ihren eigenen Fokus.
     * @param {object} evt - Event-Objekt.
     */
    handleViewerClick(evt) {
      if (this.galleryModal && this.galleryModal.hasAttribute('open')) return;

      const interactiveElement = evt.target.closest('a, button');
      if (interactiveElement) {
        interactiveElement.focus({ preventScroll: true });
        return;
      }

      if (evt.target.closest('input, textarea, select, [contenteditable], video, audio, iframe, model-viewer')) return;
      this.viewer.focus({ preventScroll: true });
    }

    /**
     * Blättert nur, wenn der Fokus innerhalb der Galerie liegt.
     * @param {object} evt - Event-Objekt.
     */
    handleKeyDown(evt) {
      const isArrowKey = evt.key === 'ArrowLeft' || evt.key === 'ArrowRight';
      const isEditable = evt.target instanceof Element
        && evt.target.closest('input, textarea, select, [contenteditable]');
      if (!isArrowKey || evt.altKey || evt.ctrlKey || evt.metaKey || isEditable) return;

      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      evt.preventDefault();
      const currentIndex = this.visibleItems.indexOf(this.currentItem);
      const nextIndex = currentIndex + (evt.key === 'ArrowRight' ? 1 : -1);
      const itemToShow = this.visibleItems[nextIndex];
      if (!itemToShow) return;

      this.customSetActiveMedia(itemToShow);

      // Lag der Fokus auf einem Thumbnail, wandert er mit. Sonst behielte das
      // zuerst angeklickte Thumbnail seinen Fokusring, während die Auswahl
      // längst weitergesprungen ist, und es sähen zwei Bilder markiert aus.
      if (this.thumbs && this.thumbs.contains(evt.target)) {
        const nextThumb = this.thumbs.querySelector(
          `[data-media-id="${itemToShow.dataset.mediaId}"] .media-thumbs__btn`,
        );
        if (nextThumb) nextThumb.focus({ preventScroll: true });
      }
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const thumbButton = thumb.querySelector('.media-thumbs__btn');
      if (thumbButton) thumbButton.focus({ preventScroll: true });

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Guard (see handleScroll): no [1] to measure with ≤1 visible item.
      if (!this.visibleItems || this.visibleItems.length <= 1) return;
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      // Order by VISUAL position, not DOM order. The variant-image filter pushes
      // pre-variant items (data-hp-pre, e.g. the title/shared shots) to the END via
      // CSS `order: 100`, so DOM order != on-screen order. handleScroll, prev/next and
      // the "x of y" counter all index visibleItems by scroll position — a DOM-ordered
      // list put the active border + counter on the WRONG item (off by the number of
      // reordered images). Sorting by offset matches the scroll-snap / flex order, so
      // every consumer lines up again. No-op for products with no reordering (offsets
      // already follow DOM order). offsetTop first covers the desktop stacked layout.
      this.visibleItems = Array.from(this.viewerItems)
        .filter((el) => el.clientWidth > 0)
        .sort((a, b) => (a.offsetTop - b.offsetTop) || (a.offsetLeft - b.offsetLeft));
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb, initial = this.hpInitialThumbAlignment) {
      cancelAnimationFrame(this.hpThumbAlignmentFrame);
      if (this.thumbs.clientWidth === 0) {
        // Versteckte/mobile Leiste erst ausrichten, sobald ihr Layout messbar ist.
        this.hpThumbAlignmentFrame = requestAnimationFrame(() => {
          // Bei dauerhaft versteckter Leiste übernimmt der nächste Resize/Klick.
          if (this.isConnected && this.thumbs.clientWidth > 0) {
            this.checkThumbVisibilty(this.currentThumb || thumb, initial);
          }
        });
        return;
      }
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: initial ? 'auto' : 'smooth' });
      }
      if (this.hpThumbFilterReady) this.hpInitialThumbAlignment = false;
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
